import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Check, Download, CreditCard } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { supabase } from "@/integrations/supabase/client";
import { getGeo } from "@/lib/geo";
import type { StyleId } from "./StyleSelector";

type Demo = { style: StyleId; image: string };

export const CheckoutDialog = ({
  open,
  onClose,
  demos,
  price,
}: {
  open: boolean;
  onClose: () => void;
  demos: Demo[];
  price: string;
}) => {
  const { t } = useI18n();
  const [stage, setStage] = useState<"idle" | "processing" | "success">("idle");

  const handlePay = async () => {
    setStage("processing");
    const g = await getGeo();
    const packageType = demos.length === 1 ? "single" : `pack-${demos.length}`;
    supabase.from("orders").insert({
      package_type: packageType,
      price,
      ip_address: g.ip,
      country_code: g.country_code,
      country_name: g.country_name,
      status: "initiated",
    });
    // Mocked Stripe flow.
    setTimeout(() => setStage("success"), 1800);
  };

  const downloadOne = (d: Demo, i: number) => {
    const a = document.createElement("a");
    a.href = d.image;
    a.download = `punchink-${d.style}-${i + 1}.png`;
    a.click();
  };

  const downloadAll = () => {
    demos.forEach((d, i) => setTimeout(() => downloadOne(d, i), i * 250));
  };

  const close = () => {
    setStage("idle");
    onClose();
  };

  const itemLabel = demos.length === 1 ? t("checkout.itemSingle") : t("checkout.itemMulti").replace("{n}", String(demos.length));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="bg-card border-neon-purple/40 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{t("checkout.title")}</DialogTitle>
        </DialogHeader>

        {stage === "idle" && (
          <div className="space-y-4">
            <div className="rounded-lg border border-border p-4 bg-background/50 space-y-2">
              <div className="flex justify-between"><span>{itemLabel}</span><span className="font-bold">{price}</span></div>
              <div className="text-xs text-muted-foreground">{t("checkout.includes")}</div>
            </div>
            <Button onClick={handlePay} size="lg" className="w-full bg-neon-green text-background hover:bg-neon-green/90 font-bold">
              <CreditCard className="w-4 h-4" /> {t("checkout.payWith")} — {price}
            </Button>
            <p className="text-xs text-muted-foreground text-center">{t("checkout.secure")}</p>
          </div>
        )}

        {stage === "processing" && (
          <div className="py-10 text-center space-y-3">
            <Loader2 className="w-10 h-10 mx-auto animate-spin text-neon-green" />
            <p>{t("checkout.processing")}</p>
          </div>
        )}

        {stage === "success" && (
          <div className="space-y-4">
            <div className="text-center space-y-3">
              <div className="w-14 h-14 mx-auto rounded-full bg-neon-green/20 grid place-items-center">
                <Check className="w-7 h-7 text-neon-green" strokeWidth={3} />
              </div>
              <p className="font-semibold">{t("checkout.success")}</p>
            </div>

            <div className={`grid gap-3 ${demos.length === 1 ? "grid-cols-1" : demos.length <= 3 ? "grid-cols-3" : "grid-cols-3"}`}>
              {demos.map((d, i) => (
                <div key={i} className="rounded-lg overflow-hidden border border-border bg-background/50">
                  <img src={d.image} alt={d.style} loading="lazy" decoding="async" className="w-full aspect-square object-cover" />
                  <div className="p-2 space-y-2">
                    <div className="text-xs font-semibold capitalize">{d.style}</div>
                    <Button
                      onClick={() => downloadOne(d, i)}
                      size="sm"
                      className="w-full bg-neon-purple/20 text-neon-purple hover:bg-neon-purple hover:text-background border border-neon-purple/40 h-8 text-xs"
                    >
                      <Download className="w-3 h-3" /> HD
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {demos.length > 1 && (
              <Button onClick={downloadAll} size="lg" className="w-full bg-neon-green text-background font-bold">
                <Download className="w-4 h-4" /> {t("checkout.downloadAll")} ({demos.length})
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
